import { useCallback, useEffect, useMemo, useState } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Fulfillment, UserProfile } from 'api';
import { ApiService } from 'api/services/ApiService';
import { ConditionalLoader, Section, ValuationStatusTracker } from 'dashboard409a/pages/Operations/components';
import Avatar from 'dashboard409a/pages/Operations/components/Avatar/Avatar';
import { ModelStatus as ModelStatusEnum } from 'dashboard409a/pages/Workboard/common/types';
import { FulfillmentQueryKeys } from 'dashboard409a/services/fulfillment';
import { AssignUsersDialog } from './components';
import { modelStatusMap, reverseModelStatusMap } from './ModelStatus.constants';
import { ModelStatusProps } from './ModelStatus.props';
import { 
  AvatarRow, 
  BlurOverlay,
  BlurredContent,
  Container, 
  CreateModelSupportButton,
  DateRow, 
  FooterBox, 
  OverlayContainer,
  RoleBox, 
  SpacedContainer, 
  Wrapper
} from './ModelStatus.styles';

const ModelStatus = ({ 
  fulfillmentData,
  isLoading,
  onCreateModelSupport,
  isCreatingModelSupport = false,
  companyId,
  valuationId,
  companyMeasurementDateId,
}: ModelStatusProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState<{ reviewers: UserProfile[], preparers: UserProfile[] }>({ reviewers: [], preparers: [] });
  
  // Get the current fulfillment data that matches the company measurement date ID
  const currentFulfillment = useMemo(() => {
    if (!fulfillmentData?.results) {
      return undefined;
    }
    
    // If we have a specific company measurement date ID, find the matching fulfillment
    if (companyMeasurementDateId) {
      return fulfillmentData.results.find(fulfillment => 
        fulfillment.company_measurement_date === companyMeasurementDateId
      );
    }

    // Fallback to first fulfillment
    return fulfillmentData.results[0];
  }, [fulfillmentData, companyMeasurementDateId]);

  // Determine if model support exists based on whether fulfillment exists and has model_status
  const hasModelSupport = useMemo(() => 
    !!currentFulfillment && currentFulfillment.model_status !== undefined,
  [currentFulfillment]);


  useEffect(() => {
    if (currentFulfillment) {
      setUsers({
        reviewers: (currentFulfillment.reviewers as UserProfile[]) || [],
        preparers: (currentFulfillment.preparers as UserProfile[]) || []
      });
    }
  }, [currentFulfillment]);

  // Helper function to get user display name
  const getUserDisplayName = useCallback((user: UserProfile): string =>  user.first_name ? `${user.first_name} ${user.last_name}` : user.email || 'Unknown User', []);

  const [tempFormData, setTempFormData] = useState<Fulfillment>(() => ({
    ...currentFulfillment,
    model_status: currentFulfillment?.model_status ?? modelStatusMap[ModelStatusEnum.DRAFT],
  } as Fulfillment));

  // Sync tempFormData with currentFulfillment when it changes
  useEffect(() => {
    if (currentFulfillment) {
      setTempFormData({
        ...currentFulfillment,
        model_status: currentFulfillment.model_status ?? modelStatusMap[ModelStatusEnum.DRAFT],
      });
    }
  }, [currentFulfillment]);

  const handleSupportingDueDateChange = (date: Date | null) => {
    // Convert to YYYY-MM-DD format for the API
    const formattedDate = date ? date.toISOString().split('T')[0] : null;
    setTempFormData({ ...tempFormData, supporting_due_date: formattedDate });
  };

  const handleModelStatusChange = (statusId: string) => {
    // Convert UI status string to API numeric value using the map
    const apiStatusValue = modelStatusMap[statusId as keyof typeof modelStatusMap];
    if (apiStatusValue !== undefined) {
      setTempFormData({ ...tempFormData, model_status: apiStatusValue as Fulfillment.model_status });
    }
  };

  const handleSave = async () => {
    try {
      if (!currentFulfillment?.id || !companyId || !valuationId) {
        throw new Error('Missing required data to save fulfillment');
      }

      setIsSaving(true);

      const updateData: Partial<Fulfillment> = {
        supporting_due_date: tempFormData.supporting_due_date,
        model_status: tempFormData.model_status,
      };

      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
        companyId,
        valuationId,
        updateData as Fulfillment
      );
      
      // Invalidate queries to refresh the UI with updated data
      await queryClient.invalidateQueries({
        queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, companyId]
      });
      
      enqueueSnackbar('Fulfillment saved successfully', { variant: 'success' });
      
    } catch (error) {
      enqueueSnackbar('Failed to save fulfillment. Please try again.', { variant: 'error' });
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  // Helper functions for display text
  const getReviewerDisplayText = () => {
    const reviewerUsers = currentFulfillment?.reviewers || [];
    if (reviewerUsers.length === 0) {
      return 'No Reviewers Assigned';
    }
    
    return reviewerUsers.map(user => getUserDisplayName(user as UserProfile)).join(', ');
  };

  const getPreparerDisplayText = () => {
    const preparerUsers = currentFulfillment?.preparers || [];
    if (preparerUsers.length === 0) {
      return 'No Preparers Assigned';
    }
    
    return preparerUsers.map(user => getUserDisplayName(user as UserProfile)).join(', ');
  };

  const handleAssignUsers = async (reviewers: number[], preparers: number[]) => {
    try {
      if (!currentFulfillment?.id || !companyId || !valuationId) {
        throw new Error('Missing required data to update fulfillment');
      }

      // Prepare the update data using the new array fields
      const updateData: Partial<Fulfillment> = {
        ...currentFulfillment,
        reviewers: reviewers.length > 0 ? reviewers : undefined,
        preparers: preparers.length > 0 ? preparers : undefined,
      };

      // The FulfillmentViewSet is implemented and uses attio_valuation_id as lookup field
      // The valuationId from URL params should be the attio_valuation_id from CompanyMeasurementDate
      await ApiService.apiDashboardCompanyOperationsFulfillmentPartialUpdate(
        companyId,
        valuationId, // This should be the attio_valuation_id from CompanyMeasurementDate
        updateData as Fulfillment
      );
      
      // Invalidate queries to refresh the UI with updated data
      await queryClient.invalidateQueries({
        queryKey: [FulfillmentQueryKeys.FULFILLMENT_LIST, companyId]
      });
      
      enqueueSnackbar('User assignments updated successfully', { variant: 'success' });
      
    } catch (error) {
      enqueueSnackbar('Failed to assign users. Please try again.', { variant: 'error' });
      throw error;
    }
  }; 


  return (
    <>
      <Section title="Model Status">
        <OverlayContainer>
          <BlurredContent hasModelSupport={hasModelSupport}>
            <Container>
              <Typography fontWeight={600}>Supporting Due Date</Typography>
              <DateRow>
                <ConditionalLoader isLoading={isLoading} width={200}>
                  <MobileDatePicker 
                    value={tempFormData?.supporting_due_date ? new Date(`${tempFormData.supporting_due_date}T00:00:00`) : null} 
                    onChange={handleSupportingDueDateChange} 
                    format="MM/dd/yyyy" 
                  />
                </ConditionalLoader>
                <CalendarMonthIcon fontSize="small" color="primary" />
              </DateRow>

              <SpacedContainer>
                <ValuationStatusTracker
                  statusOptions={Object.entries(reverseModelStatusMap).map(([, uiValue]) => ({
                    id: uiValue,
                    label: uiValue,
                  }))}
                  currentStatusId={reverseModelStatusMap[tempFormData.model_status ?? modelStatusMap[ModelStatusEnum.DRAFT] as keyof typeof reverseModelStatusMap] || ModelStatusEnum.DRAFT}
                  onStatusChange={handleModelStatusChange} 
                  isLoading={isLoading}
                />
              </SpacedContainer>

              <FooterBox>
                <RoleBox>
                  <Wrapper>
                    <Typography fontWeight={600}>Reviewer</Typography>
                    {hasModelSupport && (
                      <Tooltip title="Assign Reviewer">
                        <IconButton 
                          size="small" 
                          onClick={() => setIsAssignDialogOpen(true)}
                          disabled={isLoading}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Wrapper>
                  <AvatarRow>
                    <Avatar 
                      src="" 
                      name={getReviewerDisplayText()} 
                      roleTitle="Analyst" 
                      isLoading={isLoading} 
                    />
                  </AvatarRow>
                </RoleBox>

                <RoleBox>
                  <Wrapper>
                    <Typography fontWeight={600}>Preparer</Typography>
                    {hasModelSupport && (
                      <Tooltip title="Assign Preparer">
                        <IconButton 
                          size="small" 
                          onClick={() => setIsAssignDialogOpen(true)}
                          disabled={isLoading}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Wrapper>
                  <AvatarRow>
                    <Avatar 
                      src="" 
                      name={getPreparerDisplayText()} 
                      roleTitle="Analyst" 
                      isLoading={isLoading} 
                    />
                  </AvatarRow>
                </RoleBox>
              </FooterBox>

              {/* Save Button */}
              {hasModelSupport && (
                <Box display="flex" justifyContent="center" mt={3}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={isSaving || isLoading}
                    size="large"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </Box>
              )}
            </Container>
          </BlurredContent>
          
          {!hasModelSupport && (
            <BlurOverlay>
              <Typography variant="h6" fontWeight={600} color="text.primary" textAlign="center">
                Model Support Required
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 300 }}>
                A model support needs to be created before this area becomes available
              </Typography>
              <CreateModelSupportButton 
                onClick={onCreateModelSupport}
                variant="contained"
                disabled={isCreatingModelSupport}
              >
                {isCreatingModelSupport ? 'Creating...' : 'Create Model Support'}
              </CreateModelSupportButton>
            </BlurOverlay>
          )}
        </OverlayContainer>
      </Section>
      <AssignUsersDialog
        open={isAssignDialogOpen}
        onClose={() => setIsAssignDialogOpen(false)}
        onSave={handleAssignUsers}
        getUserDisplayName={getUserDisplayName}
        fulfillmentData={currentFulfillment}
        title="Assign Reviewers and Preparers"
        availableUsers={users}
      />
    </>
  );
};

export default ModelStatus;
